import api from './api';

// Schedule template interfaces
export interface ScheduleTemplate {
  id: string;
  user_id: string;
  name: string;
  description?: string | null;
  total_duration: number; // seconds
  video_count: number;
  is_public?: boolean;
  use_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ScheduleVideo {
  id: string;
  video_id: string;
  position: number;
  video_name?: string;
  video_duration?: number;
  video_thumbnail?: string | null;
  created_at: string;
}

export interface ScheduleTemplateWithVideos extends ScheduleTemplate {
  videos?: ScheduleVideo[];
}

export interface CreateScheduleTemplateInput {
  name: string;
  description?: string;
}

export interface UpdateScheduleTemplateInput {
  name?: string;
  description?: string;
  is_public?: boolean;
}

export interface AddVideoToScheduleInput {
  video_id: string;
  position: number;
}

export interface ReorderVideosInput {
  videos: Array<{
    video_id: string;
    position: number;
  }>;
}

export interface ListScheduleTemplatesParams {
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: 'name' | 'created_at' | 'total_duration';
  sort_order?: 'asc' | 'desc';
}

export interface ScheduleTemplateStats {
  total: number;
  total_duration: number;
  avg_videos_per_schedule: number;
}

const scheduleTemplateService = {
  /**
   * Create a new schedule template
   */
  create: async (data: CreateScheduleTemplateInput): Promise<ScheduleTemplate> => {
    const response = await api.post('/schedule-templates', data);
    return response.data.data;
  },

  /**
   * List schedule templates
   */
  list: async (params?: ListScheduleTemplatesParams) => {
    const response = await api.get('/schedule-templates', { params });
    return {
      templates: response.data.data as ScheduleTemplate[],
      pagination: response.data.pagination,
    };
  },

  /**
   * Get schedule template by ID with videos
   */
  get: async (id: string): Promise<ScheduleTemplateWithVideos> => {
    const response = await api.get(`/schedule-templates/${id}`);
    return response.data.data;
  },

  /**
   * Update schedule template
   */
  update: async (id: string, data: UpdateScheduleTemplateInput): Promise<ScheduleTemplate> => {
    const response = await api.put(`/schedule-templates/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete schedule template
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/schedule-templates/${id}`);
  },

  /**
   * Add video to schedule template
   */
  addVideo: async (templateId: string, data: AddVideoToScheduleInput): Promise<{
    schedule_video: ScheduleVideo;
    template: ScheduleTemplateWithVideos;
  }> => {
    const response = await api.post(`/schedule-templates/${templateId}/videos`, data);
    return response.data.data;
  },

  /**
   * Remove video from schedule template
   */
  removeVideo: async (templateId: string, videoId: string): Promise<ScheduleTemplateWithVideos> => {
    const response = await api.delete(`/schedule-templates/${templateId}/videos/${videoId}`);
    return response.data.data;
  },

  /**
   * Reorder videos in schedule template
   */
  reorderVideos: async (templateId: string, data: ReorderVideosInput): Promise<ScheduleTemplateWithVideos> => {
    const response = await api.put(`/schedule-templates/${templateId}/videos/reorder`, data);
    return response.data.data;
  },

  /**
   * Get schedule template statistics
   */
  getStats: async (): Promise<ScheduleTemplateStats> => {
    const response = await api.get('/schedule-templates/stats');
    return response.data.data;
  },

  /**
   * Create schedule template from CSV file
   */
  createFromCSV: async (
    file: File,
    name: string,
    description?: string
  ): Promise<ScheduleTemplateWithVideos & { stats?: any }> => {
    const formData = new FormData();
    formData.append('csv', file);
    formData.append('name', name);
    if (description) {
      formData.append('description', description);
    }

    const response = await api.post('/schedule-templates/create-from-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  /**
   * Format duration for display
   */
  formatDuration: (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  },
};

export default scheduleTemplateService;
